import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FileText, Receipt, FileCheck, Trash2, Search, Calendar,
  FolderOpen, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  "payment-invoice": FileText,
  invoice: FileText,
  receipt: Receipt,
  quote: FileCheck,
};

const labelMap = {
  "payment-invoice": "Payment Invoice",
  invoice: "Invoice",
  receipt: "Receipt",
  quote: "Quote / Proforma / Estimate",
};

export default function Library() {
  const { t } = useLanguage();
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      // First, try to load from backend
      const response = await axios.get(`${API}/documents`);
      const backendDocs = response.data || [];
      
      // Also load from localStorage as fallback
      const localDocs = JSON.parse(localStorage.getItem("utilityDocs_library") || "[]");
      
      // Merge and dedupe - backend docs take precedence
      const backendIds = new Set(backendDocs.map(d => d.id));
      const uniqueLocalDocs = localDocs.filter(d => !backendIds.has(d.id));
      
      // Combine backend docs first, then unique local docs
      const allDocs = [...backendDocs, ...uniqueLocalDocs];
      
      // Sort by date (most recent first)
      allDocs.sort((a, b) => {
        const dateA = new Date(a.savedAt || a.meta?.created_at || 0);
        const dateB = new Date(b.savedAt || b.meta?.created_at || 0);
        return dateB - dateA;
      });
      
      setDocuments(allDocs);
      
      // Update localStorage with merged data
      localStorage.setItem("utilityDocs_library", JSON.stringify(allDocs.slice(0, 50)));
    } catch (error) {
      console.error("Failed to load documents from backend:", error);
      // Fallback to localStorage only
      const localDocs = JSON.parse(localStorage.getItem("utilityDocs_library") || "[]");
      setDocuments(localDocs);
    } finally {
      setLoading(false);
    }
  };

  const deleteDocument = async (id) => {
    try {
      // Try to delete from backend
      await axios.delete(`${API}/documents/${id}`);
    } catch (error) {
      console.log("Document not in backend or already deleted");
    }
    
    // Also remove from local state and localStorage
    const filtered = documents.filter(d => d.id !== id);
    localStorage.setItem("utilityDocs_library", JSON.stringify(filtered));
    setDocuments(filtered);
    toast.success("Document deleted");
  };

  const clearAll = () => {
    localStorage.removeItem("utilityDocs_library");
    setDocuments([]);
    toast.success("Library cleared");
  };

  const filteredDocs = documents.filter(doc => {
    const matchesSearch = 
      doc.meta?.doc_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.issuer?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.recipient?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = filterType === "all" || doc.meta?.doc_type === filterType;
    
    return matchesSearch && matchesType;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency
    }).format(amount || 0);
  };

  return (
    <div className="max-w-5xl mx-auto animate-slide-in" data-testid="library-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">
            Document Library
          </h1>
          <p className="text-muted-foreground mt-1">
            Your recently created documents
          </p>
        </div>
        
        {documents.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-destructive hover:text-destructive" data-testid="clear-library-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear Library?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all documents from your local library. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={clearAll} className="bg-destructive text-destructive-foreground">
                  Clear All
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* Filters */}
      {documents.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="library-search"
            />
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={filterType === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterType("all")}
              data-testid="filter-all"
            >
              All
            </Button>
            {Object.keys(iconMap).map(type => (
              <Button
                key={type}
                variant={filterType === type ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterType(type)}
                data-testid={`filter-${type}`}
              >
                {labelMap[type]}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {/* Empty State */}
      {!loading && documents.length === 0 && (
        <div className="text-center py-16">
          <div className="w-20 h-20 rounded-full bg-muted mx-auto mb-6 flex items-center justify-center">
            <FolderOpen className="w-10 h-10 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
            No Documents Yet
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Documents you create and download will appear here for easy access.
          </p>
          <Link to="/">
            <Button data-testid="create-first-doc">
              Create Your First Document
            </Button>
          </Link>
        </div>
      )}

      {/* Documents List */}
      {filteredDocs.length > 0 && (
        <div className="space-y-3">
          {filteredDocs.map((doc) => {
            const Icon = iconMap[doc.meta?.doc_type] || FileText;
            const label = labelMap[doc.meta?.doc_type] || "Document";
            
            return (
              <div
                key={doc.id}
                className="bg-card border border-border rounded-lg p-4 hover:border-primary/30 transition-colors duration-200"
                data-testid={`doc-item-${doc.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground truncate">
                        {doc.meta?.doc_number || doc.id}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        {label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                      {doc.recipient?.name && (
                        <span className="truncate">{doc.recipient.name}</span>
                      )}
                      {doc.totals?.total > 0 && (
                        <span className="font-mono">
                          {formatCurrency(doc.totals.total, doc.meta?.currency)}
                        </span>
                      )}
                      {doc.savedAt && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(doc.savedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Link to={`/dashboard/tools/${doc.meta?.doc_type}?edit=${doc.id}`}>
                      <Button variant="outline" size="sm" data-testid={`edit-doc-${doc.id}`}>
                        Edit
                      </Button>
                    </Link>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`delete-doc-${doc.id}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Document?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove the document from your local library. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => deleteDocument(doc.id)}
                            className="bg-destructive text-destructive-foreground"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* No Results */}
      {documents.length > 0 && filteredDocs.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            No documents match your search criteria.
          </p>
        </div>
      )}
    </div>
  );
}
